import React, { ReactNode, useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  Affix,
  InputNumber,
  Select,
  Button,
  Switch,
  Divider,
  Tooltip,
  Tag,
  Alert,
  Modal,
  Input,
  Collapse,
  Radio,
  Dropdown,
} from "antd";
import type { MenuProps } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faPlus,
  faArrowDown,
  faArrowUp,
  faCopy,
  faClone,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { ModbusReadType } from "../../../flatbuffers/generated/config/modbus-read-type";
import { ModbusWriteType } from "../../../flatbuffers/generated/config/modbus-write-type";
import { ModbusInterpretation } from "../../../flatbuffers/generated/config/modbus-interpretation";
import SelectLn2Message from "../../components/select/SelectLn2Message";
import { getDisplayName } from "../../../flatbuffers/types/ln2MessageOptions";
import { RootState } from "../../../app/store";
import { findScrollTarget, findInsertionIndexAtScroll } from "./CdoLn1MappingEditor";

// ************************************************ */
// EDITOR ENTRY TYPES ***************************** */
// These mirror ModbusReadEntryT / ModbusWriteEntryT fields but replace
// ln2Message (FrontEndMessageT) with msgId+active strings for the UI.
// Conversion to/from FlatBuffer T types happens at load/save boundaries.

export interface EditorReadEntry {
  registerAddress: number;
  readType: ModbusReadType;
  quantity: number;
  interpretation: ModbusInterpretation;
  bit: number;
  bitsPerRegister: number;
  expectedValue: number;
  msgId: string;
  active: boolean;
  floorsBased: boolean;
  unitsBased: boolean;
  bankLevel: boolean;
  label: string | null;
  /** Composite key for the paired (inverse) message. null = unpaired. */
  pairedMsgId: string | null;
  /** Active flag for the paired message. */
  pairedActive: boolean;
}

export interface EditorWriteEntry {
  registerAddress: number;
  writeType: ModbusWriteType;
  interpretation: ModbusInterpretation;
  bit: number;
  bitsPerRegister: number;
  andMask: number;
  orMask: number;
  /** Fixed value to write to the Modbus register (uint16, 0–65535).
   * Only applies when interpretation=WholeValue, floorsBased=false,
   * and writeType is not MaskWriteRegister. */
  writeValue: number;
  msgId: string;
  active: boolean;
  floorsBased: boolean;
  unitsBased: boolean;
  bankLevel: boolean;
  label: string | null;
}

export interface EditorModbusTemplate {
  reads: EditorReadEntry[];
  writes: EditorWriteEntry[];
  slaveId: number | null;
  pollingInterval: bigint;
  /**
   * Per-register-space Initial offset and per-lift Delta stride, keyed
   * by Modbus register type (COIL, DI=Discrete Input, HR=Holding
   * Register, IR=Input Register). Backs the AdapterOffsetMatrix for
   * MODBUS adapters and round-trips through `ModbusRegisterOffsetMapT`
   * on the FlatBuffer wire. Optional — null/undefined means "no
   * register-level expansion", i.e. every lift reuses the entry's base
   * register address.
   */
  registerOffsets?: {
    initial: Partial<Record<"COIL" | "DI" | "HR" | "IR", number>>;
    delta: Partial<Record<"COIL" | "DI" | "HR" | "IR", number>>;
  } | null;
}

// ************************************************ */
// VIEW MODE ************************************** */
export type ViewMode = "normal" | "compact" | "expanded";

/**
 * Normalize a string for Ln2-message substring search: lowercase and strip
 * everything except alphanumerics. Lets "Usage::Up", "usage up", "usage:up"
 * all match the stored msgId "usage:normal:5" (display "Usage: Up").
 */
function normalizeLn2Needle(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

// ************************************************ */
// COLUMN STYLING ********************************* */
const MODBUS_BG = {
  border: "1px solid #1890ff",
  borderLeft: "3px solid #1890ff",
  borderRadius: 6,
  padding: 12,
};
const LN2_BG = {
  border: "1px solid #52c41a",
  borderLeft: "3px solid #52c41a",
  borderRadius: 6,
  padding: 12,
};

// ************************************************ */
// READ TYPE OPTIONS (FC 01-04) ******************* */
const READ_TYPE_OPTIONS = [
  {
    label: "Read Holding Registers (FC 03)",
    value: ModbusReadType.ReadHoldingRegisters,
  },
  { label: "Read Coils (FC 01)", value: ModbusReadType.ReadCoils },
  {
    label: "Read Discrete Inputs (FC 02)",
    value: ModbusReadType.ReadDiscreteInputs,
  },
  {
    label: "Read Input Registers (FC 04)",
    value: ModbusReadType.ReadInputRegisters,
  },
];

// ************************************************ */
// WRITE TYPE OPTIONS ***************************** */
const WRITE_TYPE_OPTIONS = [
  {
    label: "Write Single Register (FC 06)",
    value: ModbusWriteType.WriteSingleRegister,
  },
  {
    label: "Write Single Coil (FC 05)",
    value: ModbusWriteType.WriteSingleCoil,
  },
  {
    label: "Write Multiple Registers (FC 16)",
    value: ModbusWriteType.WriteMultipleRegisters,
  },
  {
    label: "Write Multiple Coils (FC 15)",
    value: ModbusWriteType.WriteMultipleCoils,
  },
  {
    label: "Mask Write Register (FC 22)",
    value: ModbusWriteType.MaskWriteRegister,
  },
];

// ************************************************ */
// INTERPRETATION OPTIONS ************************* */
const INTERPRETATION_OPTIONS = [
  { label: "Whole Value", value: ModbusInterpretation.WholeValue },
  {
    label: "Bitmask (single bit)",
    value: ModbusInterpretation.BitmaskSingleBit,
  },
];

// ************************************************ */
// HELPERS **************************************** */

function defaultReadEntry(): EditorReadEntry {
  return {
    registerAddress: 0,
    readType: ModbusReadType.ReadHoldingRegisters,
    quantity: 1,
    interpretation: ModbusInterpretation.WholeValue,
    bit: 0,
    bitsPerRegister: 16,
    expectedValue: 0,
    msgId: "",
    active: true,
    floorsBased: false,
    unitsBased: true,
    bankLevel: false,
    label: null,
    pairedMsgId: null,
    pairedActive: false,
  };
}

function defaultWriteEntry(): EditorWriteEntry {
  return {
    registerAddress: 0,
    writeType: ModbusWriteType.WriteSingleRegister,
    interpretation: ModbusInterpretation.WholeValue,
    bit: 0,
    bitsPerRegister: 16,
    andMask: 0xffff,
    orMask: 0,
    writeValue: 0,
    msgId: "",
    active: true,
    floorsBased: false,
    unitsBased: true,
    bankLevel: false,
    label: null,
  };
}

function nextReadEntry(lastRow: EditorReadEntry): EditorReadEntry {
  const base: EditorReadEntry = {
    ...defaultReadEntry(),
    readType: lastRow.readType,
    interpretation: lastRow.interpretation,
    bitsPerRegister: lastRow.bitsPerRegister,
    unitsBased: lastRow.unitsBased,
    floorsBased: lastRow.floorsBased,
    bankLevel: lastRow.bankLevel,
  };

  if (lastRow.interpretation === ModbusInterpretation.BitmaskSingleBit) {
    const nextBitRaw = lastRow.bit + 1;
    if (nextBitRaw >= lastRow.bitsPerRegister) {
      base.registerAddress = lastRow.registerAddress + 1;
      base.bit = 0;
    } else {
      base.registerAddress = lastRow.registerAddress;
      base.bit = nextBitRaw;
    }
  } else {
    base.registerAddress = lastRow.registerAddress + 1;
  }

  return base;
}

function nextWriteEntry(lastRow: EditorWriteEntry): EditorWriteEntry {
  const base: EditorWriteEntry = {
    ...defaultWriteEntry(),
    writeType: lastRow.writeType,
    interpretation: lastRow.interpretation,
    bitsPerRegister: lastRow.bitsPerRegister,
    unitsBased: lastRow.unitsBased,
    floorsBased: lastRow.floorsBased,
    bankLevel: lastRow.bankLevel,
  };

  if (lastRow.interpretation === ModbusInterpretation.BitmaskSingleBit) {
    const nextBitRaw = lastRow.bit + 1;
    if (nextBitRaw >= lastRow.bitsPerRegister) {
      base.registerAddress = lastRow.registerAddress + 1;
      base.bit = 0;
    } else {
      base.registerAddress = lastRow.registerAddress;
      base.bit = nextBitRaw;
    }
  } else {
    base.registerAddress = lastRow.registerAddress + 1;
  }

  return base;
}

// ************************************************ */
// FRONT -> REAR MAPPING ************************** */
export const FRONT_TO_REAR_MAP: Record<string, string> = {
  // Traffic
  "traffic:CarCall": "traffic:RearCarCall",
  "traffic:UpHallCall": "traffic:UpRearHallCall",
  "traffic:DownHallCall": "traffic:DownRearHallCall",
  "traffic:CarCallAnswered": "traffic:RearCarCallAnswered",
  "traffic:UpHallCallAnswered": "traffic:UpRearHallCallAnswered",
  "traffic:DownHallCallAnswered": "traffic:DownRearHallCallAnswered",
  // Control
  "control:CarCallLock": "control:RearCarCallLock",
  "control:CarCallUnlock": "control:RearCarCallUnlock",
  "control:UpHallCallLock": "control:UpRearHallCallLock",
  "control:UpHallCallUnlock": "control:UpRearHallCallUnlock",
  "control:DownHallCallLock": "control:DownRearHallCallLock",
  "control:DownHallCallUnlock": "control:DownRearHallCallUnlock",
  "control:EnergizeCarCall": "control:EnergizeRearCarCall",
  "control:EnergizeUpHallCall": "control:EnergizeUpRearHallCall",
  "control:EnergizeDownHallCall": "control:EnergizeDownRearHallCall",
  "control:SecureCarCall": "control:SecureRearCarCall",
};

/** Extract label prefix (text before first space or dash) for grouping */
function getLabelPrefix(label?: string | null): string {
  if (!label) return "";
  const match = label.match(/^([^ -]+)/);
  return match ? match[1] : label;
}

/** Build expansion info message for floorsBased / unitsBased / bankLevel */
function buildExpansionMessage(
  floorsBased: boolean,
  unitsBased: boolean,
  bankLevel: boolean,
): string | null {
  const parts: string[] = [];
  if (floorsBased) {
    parts.push("Expands per floor at generation time (bank.FloorCount entries)");
  }
  if (unitsBased) {
    parts.push("Duplicated across all lifts using bank offset_mapping");
  }
  if (bankLevel) {
    parts.push("Targets the bank as a whole (bank-level message, not per-lift)");
  }
  return parts.length > 0 ? parts.join(". ") : null;
}

// ************************************************ */
// DEFERRED INPUT (commits on Enter / blur) ******* */
interface DeferredInputProps {
  value: string;
  onCommit: (val: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}
function DeferredInput({
  value,
  onCommit,
  placeholder = "",
  style = undefined,
}: DeferredInputProps) {
  const [local, setLocal] = useState(value);
  useEffect(() => {
    setLocal(value);
  }, [value]);
  const commit = () => {
    if (local !== value) onCommit(local);
  };
  return (
    <Input
      size="small"
      placeholder={placeholder}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      onPressEnter={commit}
      style={style}
    />
  );
}

// ************************************************ */
// ADD RANGE DIALOG STATE ************************* */
interface AddRangeState {
  visible: boolean;
  mode: "read" | "write";
  startAddress: number;
  count: number;
  interpretation: ModbusInterpretation;
  readType: ModbusReadType;
  writeType: ModbusWriteType;
}

const initialAddRangeState: AddRangeState = {
  visible: false,
  mode: "read",
  startAddress: 0,
  count: 1,
  interpretation: ModbusInterpretation.WholeValue,
  readType: ModbusReadType.ReadHoldingRegisters,
  writeType: ModbusWriteType.WriteSingleRegister,
};

// ************************************************ */
// PROPS ****************************************** */
interface CdoModbusEditorProps {
  cdo: EditorModbusTemplate;
  setCdo: (val: EditorModbusTemplate) => void;
  /**
   * Render prop for the LN2 section of each read mapping row.
   *
   * When provided, replaces `SelectLn2Message` and hides Per Unit / Per Floor /
   * Active toggles (the Bank toggle remains). Used by edge management to inject
   * `Ln2MappingEditor` (bank + lift + message picker) per row instead of the
   * wizard's composite-key message picker.
   *
   * Receives the current entry and a patch callback so the injected content
   * can update entry fields directly.
   */
  renderReadLn2Section?: (
    entry: EditorReadEntry,
    update: (patch: Partial<EditorReadEntry>) => void,
  ) => ReactNode;
  /**
   * Render prop for the LN2 section of each write mapping row.
   * Same contract as `renderReadLn2Section` but for write entries.
   */
  renderWriteLn2Section?: (
    entry: EditorWriteEntry,
    update: (patch: Partial<EditorWriteEntry>) => void,
  ) => ReactNode;
}

// ************************************************ */
// COMPONENT ************************************** */
const EMPTY_TEMPLATE: EditorModbusTemplate = {
  reads: [],
  writes: [],
  slaveId: null,
  pollingInterval: BigInt(1000),
};

export default function CdoModbusEditor({
  cdo: rawCdo,
  setCdo,
  renderReadLn2Section = undefined,
  renderWriteLn2Section = undefined,
}: CdoModbusEditorProps) {
  // When a render prop is supplied the caller manages LN2 state externally —
  // wizard-only features that depend on msgId strings are disabled.
  const isWizardMode = renderReadLn2Section === undefined && renderWriteLn2Section === undefined;
  // Ensure cdo always has reads/writes arrays even if parent passes incomplete data.
  const cdo: EditorModbusTemplate = {
    ...EMPTY_TEMPLATE,
    ...rawCdo,
    reads: rawCdo?.reads ?? [],
    writes: rawCdo?.writes ?? [],
  };
  const { theme } = useSelector((state: RootState) => state.home);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [addRange, setAddRange] = useState<AddRangeState>(initialAddRangeState);
  const [intraContentMode, setIntraContentMode] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("normal");
  const [searchLn2, setSearchLn2] = useState<string>("");
  const [searchVendor, setSearchVendor] = useState<string>("");

  // ************************************************ */
  // REGISTER OVERLAP DETECTION ********************** */

  /** Set of read indices that share a register address with another read
   *  entry (different message type). When intra-content mode is ON,
   *  entries sharing a register are allowed (e.g., bitmask + whole value
   *  reading different data from the same register).
   *
   *  Wizard-only: overlap detection uses msgId strings which are absent in
   *  edge mode. Returns an empty set when a renderReadLn2Section is provided. */
  const readOverlapIndices = useMemo(() => {
    if (!isWizardMode || intraContentMode) return new Set<number>();
    // Group reads by a composite key: register address + expected value
    // (for WholeValue interpretation). Two reads at the same register with
    // different expected values are distinct — not overlapping.
    const keyToIndices = new Map<string, number[]>();
    cdo.reads.forEach((r, i) => {
      // Include readType and unitsBased in the key because different Modbus
      // function codes (FC03 vs FC04) and scopes (bank-level vs per-car)
      // are independent address spaces that resolve to different absolute
      // registers after expansion.
      const scope = `${r.readType}:${r.unitsBased ? "unit" : "bank"}`;
      let key: string;
      if (r.interpretation === ModbusInterpretation.WholeValue && r.bitsPerRegister === 8) {
        // Packed-byte reads: two reads at the same register but different byte
        // offsets (bit=0 vs bit=8) read different halves — not overlapping.
        key = `${scope}:${r.registerAddress}:byte:${r.bit}:wv:${r.expectedValue}`;
      } else if (r.interpretation === ModbusInterpretation.WholeValue) {
        key = `${scope}:${r.registerAddress}:wv:${r.expectedValue}`;
      } else if (r.interpretation === ModbusInterpretation.BitmaskSingleBit) {
        key = `${scope}:${r.registerAddress}:bit:${r.bit}`;
      } else {
        key = `${scope}:${r.registerAddress}`;
      }
      const arr = keyToIndices.get(key) || [];
      arr.push(i);
      keyToIndices.set(key, arr);
    });
    const overlaps = new Set<number>();
    keyToIndices.forEach((indices) => {
      if (indices.length > 1) {
        const msgIds = new Set(indices.map((i) => cdo.reads[i].msgId));
        if (msgIds.size > 1) {
          indices.forEach((i) => overlaps.add(i));
        }
      }
    });
    return overlaps;
  }, [isWizardMode, cdo.reads, intraContentMode]);

  // ************************************************ */
  // GLOBAL SETTINGS ******************************** */
  const onChangeSlaveId = useCallback(
    (val: number | null) => {
      setCdo({ ...cdo, slaveId: val });
    },
    [cdo, setCdo],
  );

  const onChangePollingInterval = useCallback(
    (val: number | null) => {
      setCdo({ ...cdo, pollingInterval: BigInt(val ?? 1000) });
    },
    [cdo, setCdo],
  );

  // ************************************************ */
  // INSERT POSITION ******************************** */
  // A new row is inserted at the user's current scroll position (before the
  // first row about to be visible) so it appears on screen without forcing
  // a scroll. The `data-read-index` / `data-write-index` attributes on each
  // row wrapper let us resolve viewport position back to an array index.
  const computeInsertIndex = useCallback((attr: string, len: number): number => {
    const target = findScrollTarget(rootRef.current);
    return findInsertionIndexAtScroll(target, rootRef.current, attr, len);
  }, []);

  // ************************************************ */
  // READ ENTRY HANDLERS **************************** */
  const updateReadEntry = useCallback(
    (idx: number, patch: Partial<EditorReadEntry>) => {
      const updated = [...cdo.reads];
      updated[idx] = { ...updated[idx], ...patch };
      setCdo({ ...cdo, reads: updated });
    },
    [cdo, setCdo],
  );

  const addReadEntry = useCallback(() => {
    const insertAt = computeInsertIndex("data-read-index", cdo.reads.length);
    const templateRow = insertAt > 0 ? cdo.reads[insertAt - 1] : (cdo.reads[0] ?? null);
    const newEntry = templateRow !== null ? nextReadEntry(templateRow) : defaultReadEntry();
    const updated = [...cdo.reads];
    updated.splice(insertAt, 0, newEntry);
    setCdo({ ...cdo, reads: updated });
  }, [cdo, setCdo, computeInsertIndex]);

  const removeReadEntry = useCallback(
    (idx: number) => {
      const updated = cdo.reads.filter((_, i) => i !== idx);
      setCdo({ ...cdo, reads: updated });
    },
    [cdo, setCdo],
  );

  const duplicateReadEntry = useCallback(
    (idx: number) => {
      const clone = JSON.parse(JSON.stringify(cdo.reads[idx])) as EditorReadEntry;
      setCdo({
        ...cdo,
        reads: [...cdo.reads, clone],
      });
    },
    [cdo, setCdo],
  );

  // ************************************************ */
  // WRITE ENTRY HANDLERS *************************** */
  const updateWriteEntry = useCallback(
    (idx: number, patch: Partial<EditorWriteEntry>) => {
      const updated = [...cdo.writes];
      updated[idx] = { ...updated[idx], ...patch };
      setCdo({ ...cdo, writes: updated });
    },
    [cdo, setCdo],
  );

  const addWriteEntry = useCallback(() => {
    const insertAt = computeInsertIndex("data-write-index", cdo.writes.length);
    const templateRow = insertAt > 0 ? cdo.writes[insertAt - 1] : (cdo.writes[0] ?? null);
    const newEntry = templateRow !== null ? nextWriteEntry(templateRow) : defaultWriteEntry();
    const updated = [...cdo.writes];
    updated.splice(insertAt, 0, newEntry);
    setCdo({ ...cdo, writes: updated });
  }, [cdo, setCdo, computeInsertIndex]);

  const removeWriteEntry = useCallback(
    (idx: number) => {
      const updated = cdo.writes.filter((_, i) => i !== idx);
      setCdo({ ...cdo, writes: updated });
    },
    [cdo, setCdo],
  );

  const duplicateWriteEntry = useCallback(
    (idx: number) => {
      const clone = JSON.parse(JSON.stringify(cdo.writes[idx])) as EditorWriteEntry;
      setCdo({
        ...cdo,
        writes: [...cdo.writes, clone],
      });
    },
    [cdo, setCdo],
  );

  // ************************************************ */
  // ADD RANGE HANDLERS ***************************** */
  const openAddRange = useCallback((mode: "read" | "write") => {
    setAddRange({
      visible: true,
      mode,
      startAddress: 0,
      count: 1,
      interpretation: ModbusInterpretation.WholeValue,
      readType: ModbusReadType.ReadHoldingRegisters,
      writeType: ModbusWriteType.WriteSingleRegister,
    });
  }, []);

  const closeAddRange = useCallback(() => {
    setAddRange(initialAddRangeState);
  }, []);

  const confirmAddRange = useCallback(() => {
    if (addRange.mode === "read") {
      const newEntries: EditorReadEntry[] = [];
      for (let i = 0; i < addRange.count; i += 1) {
        const addr = addRange.startAddress + i;
        newEntries.push({
          ...defaultReadEntry(),
          registerAddress: addr,
          readType: addRange.readType,
          interpretation: addRange.interpretation,
          bit: addRange.interpretation === ModbusInterpretation.BitmaskSingleBit ? 0 : 0,
        });
      }
      const insertAt = computeInsertIndex("data-read-index", cdo.reads.length);
      const updated = [...cdo.reads];
      updated.splice(insertAt, 0, ...newEntries);
      setCdo({ ...cdo, reads: updated });
    } else {
      const newEntries: EditorWriteEntry[] = [];
      for (let i = 0; i < addRange.count; i += 1) {
        const addr = addRange.startAddress + i;
        newEntries.push({
          ...defaultWriteEntry(),
          registerAddress: addr,
          writeType: addRange.writeType,
        });
      }
      const insertAt = computeInsertIndex("data-write-index", cdo.writes.length);
      const updated = [...cdo.writes];
      updated.splice(insertAt, 0, ...newEntries);
      setCdo({ ...cdo, writes: updated });
    }
    closeAddRange();
  }, [addRange, cdo, setCdo, closeAddRange, computeInsertIndex]);

  // ************************************************ */
  // MIRROR FRONT -> REAR *************************** */
  const mirrorFrontToRear = useCallback(
    (mode: "read" | "write") => {
      if (mode === "read") {
        const mirrored: EditorReadEntry[] = [];
        cdo.reads.forEach((entry) => {
          const prefix = entry.msgId.split(":").slice(0, 2).join(":");
          const rearPrefix = FRONT_TO_REAR_MAP[prefix];
          if (rearPrefix) {
            const rearMsgId = entry.msgId.replace(prefix, rearPrefix);
            const clone = JSON.parse(JSON.stringify(entry)) as EditorReadEntry;
            clone.msgId = rearMsgId;
            clone.label = clone.label ? clone.label.replace(/Front/i, "Rear") : null;
            mirrored.push(clone);
          }
        });
        if (mirrored.length > 0) {
          setCdo({
            ...cdo,
            reads: [...cdo.reads, ...mirrored],
          });
        }
      } else {
        const mirrored: EditorWriteEntry[] = [];
        cdo.writes.forEach((entry) => {
          const prefix = entry.msgId.split(":").slice(0, 2).join(":");
          const rearPrefix = FRONT_TO_REAR_MAP[prefix];
          if (rearPrefix) {
            const rearMsgId = entry.msgId.replace(prefix, rearPrefix);
            const clone = JSON.parse(JSON.stringify(entry)) as EditorWriteEntry;
            clone.msgId = rearMsgId;
            clone.label = clone.label ? clone.label.replace(/Front/i, "Rear") : null;
            mirrored.push(clone);
          }
        });
        if (mirrored.length > 0) {
          setCdo({
            ...cdo,
            writes: [...cdo.writes, ...mirrored],
          });
        }
      }
    },
    [cdo, setCdo],
  );

  // ************************************************ */
  // SEARCH FILTERING ******************************* */
  const filteredReads = useMemo(() => {
    const ln2Needle = normalizeLn2Needle(searchLn2);
    const vendorNeedle = searchVendor.trim();
    const result: { entry: EditorReadEntry; idx: number }[] = [];
    cdo.reads.forEach((entry, idx) => {
      if (ln2Needle) {
        const haystack = normalizeLn2Needle(
          `${entry.msgId} ${entry.msgId ? getDisplayName(entry.msgId) : ""}`,
        );
        if (!haystack.includes(ln2Needle)) return;
      }
      if (vendorNeedle && !String(entry.registerAddress).includes(vendorNeedle)) return;
      result.push({ entry, idx });
    });
    return result;
  }, [cdo.reads, searchLn2, searchVendor]);

  const filteredWrites = useMemo(() => {
    const ln2Needle = normalizeLn2Needle(searchLn2);
    const vendorNeedle = searchVendor.trim();
    const result: { entry: EditorWriteEntry; idx: number }[] = [];
    cdo.writes.forEach((entry, idx) => {
      if (ln2Needle) {
        const haystack = normalizeLn2Needle(
          `${entry.msgId} ${entry.msgId ? getDisplayName(entry.msgId) : ""}`,
        );
        if (!haystack.includes(ln2Needle)) return;
      }
      if (vendorNeedle && !String(entry.registerAddress).includes(vendorNeedle)) return;
      result.push({ entry, idx });
    });
    return result;
  }, [cdo.writes, searchLn2, searchVendor]);

  // ************************************************ */
  // GROUPING BY LABEL PREFIX *********************** */
  const readGroups = useMemo(
    () => groupByLabelPrefix(filteredReads, (item) => item.entry.label),
    [filteredReads],
  );

  const writeGroups = useMemo(
    () => groupByLabelPrefix(filteredWrites, (item) => item.entry.label),
    [filteredWrites],
  );

  const hasReadGroups =
    readGroups.length > 1 || (readGroups.length === 1 && readGroups[0].label !== "Ungrouped");

  const hasWriteGroups =
    writeGroups.length > 1 || (writeGroups.length === 1 && writeGroups[0].label !== "Ungrouped");

  // ************************************************ */
  // RENDER **************************************** */
  return (
    <div ref={rootRef}>
      {/* ============================================= */}
      {/* GLOBAL SETTINGS */}
      <div className="container">
        <div className="row">
          <div className="col-12 col-md-4 mt-3">
            <span className="generalStyles__inputFlexRequired">*</span>
            <span className="generalStyles__inputLabel">Slave ID</span>
            <div style={{ marginTop: 8 }}>
              <InputNumber
                size="small"
                min={0}
                max={247}
                value={cdo.slaveId}
                onChange={onChangeSlaveId}
                style={{ width: "100%" }}
                placeholder="None"
              />
            </div>
          </div>
          <div className="col-12 col-md-4 mt-3">
            <span className="generalStyles__inputFlexRequired">*</span>
            <span className="generalStyles__inputLabel">Polling Interval (ms)</span>
            <div style={{ marginTop: 8 }}>
              <InputNumber
                size="small"
                min={100}
                value={Number(cdo.pollingInterval)}
                onChange={onChangePollingInterval}
                style={{ width: "100%" }}
              />
            </div>
          </div>
          {isWizardMode && (
            <div className="col-12 col-md-4 mt-3">
              <span className="generalStyles__inputLabel">Allow Shared Registers</span>
              <Tooltip
                title={
                  intraContentMode
                    ? "ON: Multiple mappings may share the same register address (e.g., bitmask reads bits while whole-value reads the full register). Register overlap warnings are suppressed."
                    : "OFF (default): Each register address should be used by only one mapping. Shared registers are flagged as overlaps."
                }
              >
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                  <Switch
                    checked={intraContentMode}
                    onChange={setIntraContentMode}
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                  />
                  <span style={{ fontSize: 12 }}>Intra-Content Mode</span>
                  {readOverlapIndices.size > 0 && (
                    <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                      ({readOverlapIndices.size} overlap{readOverlapIndices.size === 1 ? "" : "s"})
                    </span>
                  )}
                </div>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      <br />
      {/* ============================================= */}
      {/* STICKY TOOLBAR: search + view mode + add.
          Affix pins to the nearest scroll ancestor — works inside Drawers
          where position: sticky often fails (ancestor overflow: hidden). */}
      <Affix
        offsetTop={0}
        target={() => findScrollTarget(rootRef.current)}
        style={{ zIndex: 10, marginTop: 8, marginBottom: 12 }}
      >
        <div
          className={`generalStyles__box__${theme}`}
          style={{
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 8,
            paddingRight: 8,
            borderRadius: 6,
            display: "flex",
            gap: 12,
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1 1 220px" }}>
            <span className="generalStyles__inputLabel">Search Ln2 Message</span>
            <Input
              allowClear
              size="small"
              placeholder="e.g. Usage::Up"
              value={searchLn2}
              onChange={(e) => setSearchLn2(e.target.value)}
              style={{ marginTop: 4 }}
            />
          </div>
          <div style={{ flex: "1 1 160px" }}>
            <span className="generalStyles__inputLabel">Search Register Offset</span>
            <Input
              allowClear
              size="small"
              placeholder="e.g. 1000"
              value={searchVendor}
              onChange={(e) => setSearchVendor(e.target.value)}
              style={{ marginTop: 4 }}
            />
          </div>
          <div>
            <span className="generalStyles__inputLabel" style={{ display: "block" }}>
              View
            </span>
            <Radio.Group
              size="small"
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as ViewMode)}
              options={[
                { label: "Compact", value: "compact" },
                { label: "Normal", value: "normal" },
                { label: "Expanded", value: "expanded" },
              ]}
              style={{ marginTop: 4 }}
            />
          </div>
          <div>
            <Dropdown
              menu={{
                items: [
                  { key: "read", label: "Add Read Mapping", onClick: addReadEntry },
                  { key: "write", label: "Add Write Mapping", onClick: addWriteEntry },
                ] as MenuProps["items"],
              }}
            >
              <Button
                type="primary"
                size="small"
                icon={<FontAwesomeIcon icon={faPlus} />}
                style={{ marginTop: 4 }}
              >
                Add
              </Button>
            </Dropdown>
          </div>
        </div>
      </Affix>

      {/* ============================================= */}
      {/* MODBUS -> LN2 (Read mappings) */}
      <Divider orientation="left" plain>
        <FontAwesomeIcon icon={faArrowDown} style={{ marginRight: 8 }} />
        {"Modbus \u2192 LN2 Mappings (Read from Device)"}
      </Divider>
      {isWizardMode && (
        <div style={{ textAlign: "right", marginBottom: 8 }}>
          <Tooltip title="Duplicate front-door read mappings as rear-door equivalents">
            <Button size="small" onClick={() => mirrorFrontToRear("read")}>
              <FontAwesomeIcon icon={faClone} style={{ marginRight: 6 }} />
              Mirror Front → Rear
            </Button>
          </Tooltip>
        </div>
      )}

      {hasReadGroups ? (
        <Collapse defaultActiveKey={readGroups.map((g) => g.label)}>
          {readGroups.map((group) => (
            <Collapse.Panel
              key={group.label}
              header={`${group.label} (${String(group.items.length)})`}
            >
              {group.items.map(({ entry, idx }) => (
                <ReadMappingRow
                  key={`read-${String(idx)}`}
                  entry={entry}
                  index={idx}
                  onUpdate={updateReadEntry}
                  onRemove={removeReadEntry}
                  onDuplicate={duplicateReadEntry}
                  hasOverlap={readOverlapIndices.has(idx)}
                  renderLn2Section={renderReadLn2Section}
                  viewMode={viewMode}
                />
              ))}
            </Collapse.Panel>
          ))}
        </Collapse>
      ) : (
        filteredReads.map(({ entry, idx }) => (
          <ReadMappingRow
            key={`read-${String(idx)}`}
            entry={entry}
            index={idx}
            onUpdate={updateReadEntry}
            onRemove={removeReadEntry}
            onDuplicate={duplicateReadEntry}
            hasOverlap={readOverlapIndices.has(idx)}
            renderLn2Section={renderReadLn2Section}
            viewMode={viewMode}
          />
        ))
      )}

      <div
        style={{
          textAlign: "center",
          marginTop: 12,
          marginBottom: 8,
          display: "flex",
          justifyContent: "center",
          gap: 8,
        }}
      >
        <Button type="dashed" onClick={addReadEntry}>
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
          Add Read Mapping
        </Button>
        <Button type="dashed" onClick={() => openAddRange("read")}>
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
          Add Range
        </Button>
      </div>

      {/* ============================================= */}
      {/* LN2 -> MODBUS (Write mappings) */}
      <Divider orientation="left" plain>
        <FontAwesomeIcon icon={faArrowUp} style={{ marginRight: 8 }} />
        {"LN2 \u2192 Modbus Mappings (Write to Device)"}
      </Divider>
      {isWizardMode && (
        <div style={{ textAlign: "right", marginBottom: 8 }}>
          <Tooltip title="Duplicate front-door write mappings as rear-door equivalents">
            <Button size="small" onClick={() => mirrorFrontToRear("write")}>
              <FontAwesomeIcon icon={faClone} style={{ marginRight: 6 }} />
              Mirror Front → Rear
            </Button>
          </Tooltip>
        </div>
      )}

      {hasWriteGroups ? (
        <Collapse defaultActiveKey={writeGroups.map((g) => g.label)}>
          {writeGroups.map((group) => (
            <Collapse.Panel
              key={group.label}
              header={`${group.label} (${String(group.items.length)})`}
            >
              {group.items.map(({ entry, idx }) => (
                <WriteMappingRow
                  key={`write-${String(idx)}`}
                  entry={entry}
                  index={idx}
                  onUpdate={updateWriteEntry}
                  onRemove={removeWriteEntry}
                  onDuplicate={duplicateWriteEntry}
                  renderLn2Section={renderWriteLn2Section}
                  viewMode={viewMode}
                />
              ))}
            </Collapse.Panel>
          ))}
        </Collapse>
      ) : (
        filteredWrites.map(({ entry, idx }) => (
          <WriteMappingRow
            key={`write-${String(idx)}`}
            entry={entry}
            index={idx}
            onUpdate={updateWriteEntry}
            onRemove={removeWriteEntry}
            onDuplicate={duplicateWriteEntry}
            renderLn2Section={renderWriteLn2Section}
            viewMode={viewMode}
          />
        ))
      )}

      <div
        style={{
          textAlign: "center",
          marginTop: 12,
          marginBottom: 8,
          display: "flex",
          justifyContent: "center",
          gap: 8,
        }}
      >
        <Button type="dashed" onClick={addWriteEntry}>
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
          Add Write Mapping
        </Button>
        <Button type="dashed" onClick={() => openAddRange("write")}>
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
          Add Range
        </Button>
      </div>

      {/* ============================================= */}
      {/* ADD RANGE MODAL */}
      <Modal
        title={addRange.mode === "read" ? "Add Read Mapping Range" : "Add Write Mapping Range"}
        open={addRange.visible}
        onOk={confirmAddRange}
        onCancel={closeAddRange}
        okText="Generate"
      >
        <div className="container">
          <div className="row">
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputLabel">Start Address</span>
              <InputNumber
                className="generalStyles__width100"
                size="small"
                min={0}
                max={65535}
                value={addRange.startAddress}
                onChange={(val) =>
                  setAddRange((prev) => ({
                    ...prev,
                    startAddress: val ?? 0,
                  }))
                }
                style={{ marginTop: 4 }}
              />
            </div>
            <div className="col-12 col-md-6 mt-3">
              <span className="generalStyles__inputLabel">Count</span>
              <InputNumber
                className="generalStyles__width100"
                size="small"
                min={1}
                max={100}
                value={addRange.count}
                onChange={(val) => setAddRange((prev) => ({ ...prev, count: val ?? 1 }))}
                style={{ marginTop: 4 }}
              />
            </div>
          </div>
          <div className="row">
            {addRange.mode === "read" && (
              <div className="col-12 col-md-6 mt-3">
                <span className="generalStyles__inputLabel">Interpretation</span>
                <Select
                  className="generalStyles__width100"
                  size="small"
                  value={addRange.interpretation}
                  onChange={(val) =>
                    setAddRange((prev) => ({
                      ...prev,
                      interpretation: val,
                    }))
                  }
                  style={{ marginTop: 4 }}
                  options={INTERPRETATION_OPTIONS}
                />
              </div>
            )}
            <div className={`col-12 ${addRange.mode === "read" ? "col-md-6" : "col-md-12"} mt-3`}>
              <span className="generalStyles__inputLabel">Request Type</span>
              {addRange.mode === "read" ? (
                <Select
                  className="generalStyles__width100"
                  size="small"
                  value={addRange.readType}
                  onChange={(val) => setAddRange((prev) => ({ ...prev, readType: val }))}
                  style={{ marginTop: 4 }}
                  options={READ_TYPE_OPTIONS}
                />
              ) : (
                <Select
                  className="generalStyles__width100"
                  size="small"
                  value={addRange.writeType}
                  onChange={(val) => setAddRange((prev) => ({ ...prev, writeType: val }))}
                  style={{ marginTop: 4 }}
                  options={WRITE_TYPE_OPTIONS}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ************************************************ */
// GROUP BY LABEL PREFIX UTILITY ****************** */
interface LabelGroup<T> {
  label: string;
  items: T[];
}

function groupByLabelPrefix<T>(
  items: T[],
  getLabel: (item: T) => string | null | undefined,
): LabelGroup<T>[] {
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const prefix = getLabelPrefix(getLabel(item)) || "Ungrouped";
    const existing = groups.get(prefix);
    if (existing) {
      existing.push(item);
    } else {
      groups.set(prefix, [item]);
    }
  });
  const result: LabelGroup<T>[] = [];
  Array.from(groups.entries()).forEach(([label, groupItems]) => {
    result.push({ label, items: groupItems });
  });
  return result;
}

// ************************************************ */
// READ MAPPING ROW (Modbus -> LN2) *************** */
interface ReadMappingRowProps {
  entry: EditorReadEntry;
  index: number;
  onUpdate: (idx: number, patch: Partial<EditorReadEntry>) => void;
  onRemove: (idx: number) => void;
  onDuplicate: (idx: number) => void;
  hasOverlap?: boolean;
  /** When provided, replaces SelectLn2Message and hides Per Unit/Per Floor/Active toggles. */
  renderLn2Section?: (
    entry: EditorReadEntry,
    update: (patch: Partial<EditorReadEntry>) => void,
  ) => ReactNode;
  viewMode?: ViewMode;
}

function ReadMappingRow({
  entry,
  index,
  onUpdate,
  onRemove,
  onDuplicate,
  hasOverlap = false,
  renderLn2Section = undefined,
  viewMode = "normal",
}: ReadMappingRowProps) {
  const isBitmask = entry.interpretation === ModbusInterpretation.BitmaskSingleBit;
  const isPerFloor = entry.floorsBased;
  const isPerUnit = entry.unitsBased;
  const isExpandedTarget = isPerFloor || isPerUnit;
  const expansionMsg = buildExpansionMessage(entry.floorsBased, entry.unitsBased, entry.bankLevel);
  const autoStyle: React.CSSProperties = { opacity: 0.45 };

  if (viewMode === "compact") {
    const fixedItem: React.CSSProperties = { flexShrink: 0 };
    const bitInputStyle: React.CSSProperties = {
      width: 64,
      flexShrink: 0,
      ...(isPerFloor ? autoStyle : {}),
    };
    const valueInputStyle: React.CSSProperties = {
      width: 72,
      flexShrink: 0,
      ...(isPerFloor ? autoStyle : {}),
    };
    return (
      <div
        data-read-index={index}
        style={{
          border: hasOverlap ? "2px solid #ff4d4f" : "1px solid #d9d9d9",
          borderRadius: 6,
          padding: "8px 10px",
          marginBottom: 6,
          display: "flex",
          flexWrap: "wrap",
          columnGap: 8,
          rowGap: 6,
          alignItems: "center",
          alignContent: "flex-start",
          minHeight: 40,
          ...(hasOverlap ? { boxShadow: "0 0 6px 1px rgba(255, 77, 79, 0.4)" } : {}),
        }}
      >
        <Tag color="blue" style={{ margin: 0, flexShrink: 0 }}>
          R#{index + 1}
        </Tag>
        <InputNumber
          size="small"
          min={0}
          max={65535}
          value={entry.registerAddress}
          onChange={(val) => onUpdate(index, { registerAddress: val ?? 0 })}
          style={{ width: 96, ...fixedItem }}
        />
        <Select
          size="small"
          value={entry.readType}
          onChange={(val) => onUpdate(index, { readType: val })}
          options={READ_TYPE_OPTIONS}
          style={{ width: 120, ...fixedItem }}
          dropdownMatchSelectWidth={260}
        />
        <Select
          size="small"
          value={entry.interpretation}
          onChange={(val) => {
            const patch: Partial<EditorReadEntry> = { interpretation: val };
            if (val === ModbusInterpretation.BitmaskSingleBit) {
              patch.bit = 0;
              patch.bitsPerRegister = 16;
              patch.expectedValue = 0;
            } else {
              patch.bit = 0;
              patch.bitsPerRegister = 16;
            }
            onUpdate(index, patch);
          }}
          options={INTERPRETATION_OPTIONS}
          style={{ width: 140, ...fixedItem }}
          dropdownMatchSelectWidth={240}
        />
        {isBitmask ? (
          <InputNumber
            size="small"
            min={0}
            max={15}
            disabled={isPerFloor}
            value={isPerFloor ? undefined : entry.bit}
            placeholder="bit"
            onChange={(val) => onUpdate(index, { bit: val ?? 0 })}
            style={bitInputStyle}
          />
        ) : (
          <InputNumber
            size="small"
            min={0}
            max={65535}
            disabled={isPerFloor}
            value={isPerFloor ? undefined : entry.expectedValue}
            placeholder="val"
            onChange={(val) => onUpdate(index, { expectedValue: val ?? 0 })}
            style={valueInputStyle}
          />
        )}
        <div style={{ flex: "1 1 200px", minWidth: 200 }}>
          {renderLn2Section !== undefined ? (
            renderLn2Section(entry, (patch) => onUpdate(index, patch))
          ) : (
            <SelectLn2Message
              value={entry.msgId}
              onChange={(key: string) => onUpdate(index, { msgId: key })}
              disabled={false}
            />
          )}
        </div>
        <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
          {renderLn2Section === undefined && (
            <>
              <Tooltip title="Per Unit">
                <Switch
                  size="small"
                  checked={entry.unitsBased}
                  onChange={(checked) => onUpdate(index, { unitsBased: checked })}
                  checkedChildren="U"
                  unCheckedChildren="U"
                />
              </Tooltip>
              <Tooltip title="Per Floor">
                <Switch
                  size="small"
                  checked={entry.floorsBased}
                  onChange={(checked) => onUpdate(index, { floorsBased: checked })}
                  checkedChildren="F"
                  unCheckedChildren="F"
                />
              </Tooltip>
              <Tooltip title="Active">
                <Switch
                  size="small"
                  checked={entry.active}
                  onChange={(checked) => onUpdate(index, { active: checked })}
                  checkedChildren="A"
                  unCheckedChildren="A"
                />
              </Tooltip>
            </>
          )}
          <Tooltip title="Bank-level (applies to bank, not per-lift)">
            <Switch
              size="small"
              checked={entry.bankLevel}
              onChange={(checked) => onUpdate(index, { bankLevel: checked })}
              checkedChildren="B"
              unCheckedChildren="B"
            />
          </Tooltip>
        </span>
        <span style={{ display: "inline-flex", gap: 2, alignItems: "center", flexShrink: 0 }}>
          <Tooltip title="Duplicate mapping">
            <Button type="text" size="small" onClick={() => onDuplicate(index)}>
              <FontAwesomeIcon icon={faCopy} />
            </Button>
          </Tooltip>
          <Tooltip title="Remove mapping">
            <Button type="text" danger size="small" onClick={() => onRemove(index)}>
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Tooltip>
        </span>
      </div>
    );
  }

  const isExpandedView = viewMode === "expanded";

  return (
    <div
      data-read-index={index}
      style={{
        border: hasOverlap ? "2px solid #ff4d4f" : "1px solid #d9d9d9",
        borderRadius: 6,
        padding: 12,
        marginBottom: 8,
        position: "relative",
        ...(hasOverlap ? { boxShadow: "0 0 8px 2px rgba(255, 77, 79, 0.5)" } : {}),
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tag color="blue">Read #{index + 1}</Tag>
          {isExpandedView && (
            <DeferredInput
              placeholder="Label (used for grouping)"
              value={entry.label ?? ""}
              onCommit={(val) => onUpdate(index, { label: val })}
              style={{ width: 220 }}
            />
          )}
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <Tooltip title="Duplicate mapping">
            <Button type="text" size="small" onClick={() => onDuplicate(index)}>
              <FontAwesomeIcon icon={faCopy} />
            </Button>
          </Tooltip>
          <Tooltip title="Remove mapping">
            <Button type="text" danger size="small" onClick={() => onRemove(index)}>
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="container">
        <div className="row">
          {/* LEFT: Modbus settings (blue) */}
          <div className="col-12 col-md-6 mt-2">
            <div style={MODBUS_BG}>
              <div className="row">
                <div className="col-12 col-md-4">
                  <span className="generalStyles__inputLabel">
                    Register Address
                    {isExpandedTarget && <span style={autoStyle}> (base)</span>}
                  </span>
                  <InputNumber
                    className="generalStyles__width100"
                    size="small"
                    min={0}
                    max={65535}
                    value={entry.registerAddress}
                    onChange={(val) => onUpdate(index, { registerAddress: val ?? 0 })}
                    style={{ marginTop: 4 }}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <span className="generalStyles__inputLabel">Read Type</span>
                  <Select
                    className="generalStyles__width100"
                    size="small"
                    value={entry.readType}
                    onChange={(val) => onUpdate(index, { readType: val })}
                    style={{ marginTop: 4 }}
                    options={READ_TYPE_OPTIONS}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <span className="generalStyles__inputLabel">
                    Quantity
                    {isPerFloor && <span style={autoStyle}> (auto)</span>}
                  </span>
                  <InputNumber
                    className="generalStyles__width100"
                    size="small"
                    min={1}
                    max={125}
                    disabled={isPerFloor}
                    value={entry.quantity}
                    onChange={(val) => onUpdate(index, { quantity: val ?? 1 })}
                    style={{ marginTop: 4, ...(isPerFloor ? autoStyle : {}) }}
                  />
                </div>
              </div>
              <div className="row" style={{ marginTop: 8 }}>
                <div className={`col-12 ${isBitmask ? "col-md-6" : "col-md-12"}`}>
                  <span className="generalStyles__inputLabel">Interpretation</span>
                  <Select
                    className="generalStyles__width100"
                    size="small"
                    value={entry.interpretation}
                    onChange={(val) => {
                      const patch: Partial<EditorReadEntry> = {
                        interpretation: val,
                      };
                      if (val === ModbusInterpretation.BitmaskSingleBit) {
                        patch.bit = 0;
                        patch.bitsPerRegister = 16;
                        patch.expectedValue = 0;
                      } else {
                        patch.bit = 0;
                        patch.bitsPerRegister = 16;
                      }
                      onUpdate(index, patch);
                    }}
                    style={{ marginTop: 4 }}
                    options={INTERPRETATION_OPTIONS}
                  />
                </div>
                {!isBitmask && (
                  <div className="col-12 col-md-3">
                    <span className="generalStyles__inputLabel">
                      Expected Value
                      {isPerFloor && <span style={autoStyle}> (auto)</span>}
                    </span>
                    <InputNumber
                      className="generalStyles__width100"
                      size="small"
                      min={0}
                      max={65535}
                      disabled={isPerFloor}
                      placeholder={isPerFloor ? "Floor index" : undefined}
                      value={isPerFloor ? undefined : entry.expectedValue}
                      onChange={(val) => onUpdate(index, { expectedValue: val ?? 0 })}
                      style={{ marginTop: 4, ...(isPerFloor ? autoStyle : {}) }}
                    />
                  </div>
                )}
                {isBitmask && (
                  <>
                    <div className="col-12 col-md-3">
                      <span className="generalStyles__inputLabel">
                        Bit
                        {isPerFloor ? <span style={autoStyle}> (auto)</span> : " (0-15)"}
                      </span>
                      <InputNumber
                        className="generalStyles__width100"
                        size="small"
                        min={0}
                        max={15}
                        disabled={isPerFloor}
                        placeholder={isPerFloor ? "Auto" : undefined}
                        value={isPerFloor ? undefined : entry.bit}
                        onChange={(val) => onUpdate(index, { bit: val ?? 0 })}
                        style={{
                          marginTop: 4,
                          ...(isPerFloor ? autoStyle : {}),
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-3">
                      <span className="generalStyles__inputLabel">Bits/Register</span>
                      <Select
                        className="generalStyles__width100"
                        size="small"
                        value={entry.bitsPerRegister}
                        onChange={(val) => onUpdate(index, { bitsPerRegister: val })}
                        style={{ marginTop: 4 }}
                        options={[
                          { label: "8-bit", value: 8 },
                          { label: "16-bit", value: 16 },
                        ]}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: LN2 settings (green) */}
          <div className="col-12 col-md-6 mt-2">
            <div style={LN2_BG}>
              {renderLn2Section !== undefined ? (
                <>
                  {renderLn2Section(entry, (patch) => onUpdate(index, patch))}
                  {/* Bank toggle remains visible in edge mode */}
                  <div className="row" style={{ marginTop: 8 }}>
                    <div className="col-12 col-md-3">
                      <Tooltip title="Targets the bank as a whole (bank-level message, not per-lift)">
                        <span className="generalStyles__inputLabel">Bank</span>
                      </Tooltip>
                      <div style={{ marginTop: 8 }}>
                        <Switch
                          checked={entry.bankLevel}
                          onChange={(checked) => onUpdate(index, { bankLevel: checked })}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="row">
                    <div className="col-12">
                      <span className="generalStyles__inputLabel">
                        LN2 Message{isPerFloor ? " (auto-assigned per floor)" : ""}
                      </span>
                      <div style={{ marginTop: 4 }}>
                        <SelectLn2Message
                          value={entry.msgId}
                          onChange={(key: string) => onUpdate(index, { msgId: key })}
                          disabled={false}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row" style={{ marginTop: 8 }}>
                    <div className="col-12 col-md-3">
                      <span className="generalStyles__inputLabel">Per Unit</span>
                      <div style={{ marginTop: 8 }}>
                        <Switch
                          checked={entry.unitsBased}
                          onChange={(checked) => onUpdate(index, { unitsBased: checked })}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <span className="generalStyles__inputLabel">Per Floor</span>
                      <div style={{ marginTop: 8 }}>
                        <Switch
                          checked={entry.floorsBased}
                          onChange={(checked) => onUpdate(index, { floorsBased: checked })}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <span className="generalStyles__inputLabel">Active</span>
                      <div style={{ marginTop: 8 }}>
                        <Switch
                          checked={entry.active}
                          onChange={(checked) => onUpdate(index, { active: checked })}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <Tooltip title="Targets the bank as a whole (bank-level message, not per-lift)">
                        <span className="generalStyles__inputLabel">Bank</span>
                      </Tooltip>
                      <div style={{ marginTop: 8 }}>
                        <Switch
                          checked={entry.bankLevel}
                          onChange={(checked) => onUpdate(index, { bankLevel: checked })}
                        />
                      </div>
                    </div>
                  </div>
                  {(expansionMsg || isExpandedView) && (
                    <div style={{ marginTop: 8 }}>
                      <Alert
                        type="info"
                        showIcon
                        message={
                          expansionMsg ||
                          "Single-instance mapping (not per-unit, per-floor, or bank-level)"
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ************************************************ */
// WRITE MAPPING ROW (LN2 -> Modbus) ************** */
interface WriteMappingRowProps {
  entry: EditorWriteEntry;
  index: number;
  onUpdate: (idx: number, patch: Partial<EditorWriteEntry>) => void;
  onRemove: (idx: number) => void;
  onDuplicate: (idx: number) => void;
  /** When provided, replaces SelectLn2Message and hides Per Unit/Per Floor/Active toggles. */
  renderLn2Section?: (
    entry: EditorWriteEntry,
    update: (patch: Partial<EditorWriteEntry>) => void,
  ) => ReactNode;
  viewMode?: ViewMode;
}

function WriteMappingRow({
  entry,
  index,
  onUpdate,
  onRemove,
  onDuplicate,
  renderLn2Section = undefined,
  viewMode = "normal",
}: WriteMappingRowProps) {
  const isBitmask = entry.interpretation === ModbusInterpretation.BitmaskSingleBit;
  const isPerFloor = entry.floorsBased;
  const showWriteValue =
    !isBitmask && !isPerFloor && entry.writeType !== ModbusWriteType.MaskWriteRegister;
  const expansionMsg = buildExpansionMessage(entry.floorsBased, entry.unitsBased, entry.bankLevel);

  if (viewMode === "compact") {
    const fixedItem: React.CSSProperties = { flexShrink: 0 };
    return (
      <div
        data-write-index={index}
        style={{
          border: "1px solid #d9d9d9",
          borderRadius: 6,
          padding: "8px 10px",
          marginBottom: 6,
          display: "flex",
          flexWrap: "wrap",
          columnGap: 8,
          rowGap: 6,
          alignItems: "center",
          alignContent: "flex-start",
          minHeight: 40,
        }}
      >
        <Tag color="green" style={{ margin: 0, flexShrink: 0 }}>
          W#{index + 1}
        </Tag>
        <DeferredInput
          placeholder="Label"
          value={entry.label ?? ""}
          onCommit={(val) => onUpdate(index, { label: val })}
          style={{ width: 130, ...fixedItem }}
        />
        <Select
          size="small"
          value={entry.writeType}
          onChange={(val) => onUpdate(index, { writeType: val })}
          options={WRITE_TYPE_OPTIONS}
          style={{ width: 150, ...fixedItem }}
          dropdownMatchSelectWidth={260}
        />
        <InputNumber
          size="small"
          min={0}
          max={65535}
          value={entry.registerAddress}
          onChange={(val) => onUpdate(index, { registerAddress: val ?? 0 })}
          style={{ width: 96, ...fixedItem }}
        />
        <Select
          size="small"
          value={entry.interpretation}
          onChange={(val) =>
            onUpdate(index, {
              interpretation: val,
              bit: 0,
              bitsPerRegister: 16,
            })
          }
          options={INTERPRETATION_OPTIONS}
          style={{ width: 140, ...fixedItem }}
          dropdownMatchSelectWidth={240}
        />
        {isBitmask && (
          <InputNumber
            size="small"
            min={0}
            max={15}
            disabled={isPerFloor}
            value={entry.bit}
            placeholder="bit"
            onChange={(val) => onUpdate(index, { bit: val ?? 0 })}
            style={{ width: 64, ...fixedItem }}
          />
        )}
        {showWriteValue && (
          <InputNumber
            size="small"
            min={0}
            max={65535}
            value={entry.writeValue}
            placeholder="write"
            onChange={(val) => onUpdate(index, { writeValue: val ?? 0 })}
            style={{ width: 80, ...fixedItem }}
          />
        )}
        <div style={{ flex: "1 1 200px", minWidth: 200 }}>
          {renderLn2Section !== undefined ? (
            renderLn2Section(entry, (patch) => onUpdate(index, patch))
          ) : (
            <SelectLn2Message
              value={entry.msgId}
              onChange={(key: string) => onUpdate(index, { msgId: key })}
              disabled={false}
            />
          )}
        </div>
        <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
          {renderLn2Section === undefined && (
            <>
              <Tooltip title="Per Unit">
                <Switch
                  size="small"
                  checked={entry.unitsBased}
                  onChange={(checked) => onUpdate(index, { unitsBased: checked })}
                  checkedChildren="U"
                  unCheckedChildren="U"
                />
              </Tooltip>
              <Tooltip title="Per Floor">
                <Switch
                  size="small"
                  checked={entry.floorsBased}
                  onChange={(checked) => onUpdate(index, { floorsBased: checked })}
                  checkedChildren="F"
                  unCheckedChildren="F"
                />
              </Tooltip>
              <Tooltip title="Active">
                <Switch
                  size="small"
                  checked={entry.active}
                  onChange={(checked) => onUpdate(index, { active: checked })}
                  checkedChildren="A"
                  unCheckedChildren="A"
                />
              </Tooltip>
            </>
          )}
          <Tooltip title="Bank-level (applies to bank, not per-lift)">
            <Switch
              size="small"
              checked={entry.bankLevel}
              onChange={(checked) => onUpdate(index, { bankLevel: checked })}
              checkedChildren="B"
              unCheckedChildren="B"
            />
          </Tooltip>
        </span>
        <span style={{ display: "inline-flex", gap: 2, alignItems: "center", flexShrink: 0 }}>
          <Tooltip title="Duplicate mapping">
            <Button type="text" size="small" onClick={() => onDuplicate(index)}>
              <FontAwesomeIcon icon={faCopy} />
            </Button>
          </Tooltip>
          <Tooltip title="Remove mapping">
            <Button type="text" danger size="small" onClick={() => onRemove(index)}>
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Tooltip>
        </span>
      </div>
    );
  }

  const isExpandedView = viewMode === "expanded";

  return (
    <div
      data-write-index={index}
      style={{
        border: "1px solid #d9d9d9",
        borderRadius: 6,
        padding: 12,
        marginBottom: 8,
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tag color="green">Write #{index + 1}</Tag>
          <DeferredInput
            placeholder="Label"
            value={entry.label ?? ""}
            onCommit={(val) => onUpdate(index, { label: val })}
            style={{ width: 200 }}
          />
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <Tooltip title="Duplicate mapping">
            <Button type="text" size="small" onClick={() => onDuplicate(index)}>
              <FontAwesomeIcon icon={faCopy} />
            </Button>
          </Tooltip>
          <Tooltip title="Remove mapping">
            <Button type="text" danger size="small" onClick={() => onRemove(index)}>
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="container">
        <div className="row">
          {/* LEFT: Modbus settings (blue) */}
          <div className="col-12 col-md-6 mt-2">
            <div style={MODBUS_BG}>
              <div className="row">
                <div className="col-12 col-md-8">
                  <span className="generalStyles__inputLabel">Write Type</span>
                  <Select
                    className="generalStyles__width100"
                    size="small"
                    value={entry.writeType}
                    onChange={(val) => onUpdate(index, { writeType: val })}
                    style={{ marginTop: 4 }}
                    options={WRITE_TYPE_OPTIONS}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <span className="generalStyles__inputLabel">Address</span>
                  <InputNumber
                    className="generalStyles__width100"
                    size="small"
                    min={0}
                    max={65535}
                    value={entry.registerAddress}
                    onChange={(val) => onUpdate(index, { registerAddress: val ?? 0 })}
                    style={{ marginTop: 4 }}
                  />
                </div>
              </div>
              {showWriteValue && (
                <div className="row" style={{ marginTop: 8 }}>
                  <Tooltip title="Fixed value written to the Modbus register when this LN2 message fires. Range: 0–65535 (uint16). Not used for floor-based expansion, bitmask entries, or mask-write operations.">
                    <div className="col-12 col-md-4">
                      <span className="generalStyles__inputLabel">Write Value</span>
                      <InputNumber
                        className="generalStyles__width100"
                        size="small"
                        min={0}
                        max={65535}
                        value={entry.writeValue}
                        onChange={(val) => onUpdate(index, { writeValue: val ?? 0 })}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                  </Tooltip>
                </div>
              )}
              <div className="row" style={{ marginTop: 8 }}>
                <div className={`col-12 ${isBitmask ? "col-md-6" : "col-md-12"}`}>
                  <span className="generalStyles__inputLabel">Interpretation</span>
                  <Select
                    className="generalStyles__width100"
                    size="small"
                    value={entry.interpretation}
                    onChange={(val) => {
                      onUpdate(index, {
                        interpretation: val,
                        bit: 0,
                        bitsPerRegister: 16,
                      });
                    }}
                    style={{ marginTop: 4 }}
                    options={INTERPRETATION_OPTIONS}
                  />
                </div>
                {isBitmask && (
                  <>
                    <div className="col-12 col-md-3">
                      <span className="generalStyles__inputLabel">
                        {isPerFloor ? "Bit (auto)" : "Bit (0-15)"}
                      </span>
                      <InputNumber
                        className="generalStyles__width100"
                        size="small"
                        min={0}
                        max={15}
                        disabled={isPerFloor}
                        placeholder={isPerFloor ? "Auto" : undefined}
                        value={entry.bit}
                        onChange={(val) => onUpdate(index, { bit: val ?? 0 })}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                    <div className="col-12 col-md-3">
                      <span className="generalStyles__inputLabel">Bits/Register</span>
                      <Select
                        className="generalStyles__width100"
                        size="small"
                        value={entry.bitsPerRegister}
                        onChange={(val) => onUpdate(index, { bitsPerRegister: val })}
                        style={{ marginTop: 4 }}
                        options={[
                          { label: "8-bit", value: 8 },
                          { label: "16-bit", value: 16 },
                        ]}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: LN2 settings (green) */}
          <div className="col-12 col-md-6 mt-2">
            <div style={LN2_BG}>
              {renderLn2Section !== undefined ? (
                <>
                  {renderLn2Section(entry, (patch) => onUpdate(index, patch))}
                  {/* Bank toggle remains visible in edge mode */}
                  <div className="row" style={{ marginTop: 8 }}>
                    <div className="col-12 col-md-3">
                      <Tooltip title="Targets the bank as a whole (bank-level message, not per-lift)">
                        <span className="generalStyles__inputLabel">Bank</span>
                      </Tooltip>
                      <div style={{ marginTop: 8 }}>
                        <Switch
                          checked={entry.bankLevel}
                          onChange={(checked) => onUpdate(index, { bankLevel: checked })}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="row">
                    <div className="col-12">
                      <span className="generalStyles__inputLabel">
                        LN2 Message{isPerFloor ? " (auto-assigned per floor)" : ""}
                      </span>
                      <div style={{ marginTop: 4 }}>
                        <SelectLn2Message
                          value={entry.msgId}
                          onChange={(key: string) => onUpdate(index, { msgId: key })}
                          disabled={false}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row" style={{ marginTop: 8 }}>
                    <div className="col-12 col-md-3">
                      <span className="generalStyles__inputLabel">Per Unit</span>
                      <div style={{ marginTop: 8 }}>
                        <Switch
                          checked={entry.unitsBased}
                          onChange={(checked) => onUpdate(index, { unitsBased: checked })}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <span className="generalStyles__inputLabel">Per Floor</span>
                      <div style={{ marginTop: 8 }}>
                        <Switch
                          checked={entry.floorsBased}
                          onChange={(checked) => onUpdate(index, { floorsBased: checked })}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <span className="generalStyles__inputLabel">Active</span>
                      <div style={{ marginTop: 8 }}>
                        <Switch
                          checked={entry.active}
                          onChange={(checked) => onUpdate(index, { active: checked })}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <Tooltip title="Targets the bank as a whole (bank-level message, not per-lift)">
                        <span className="generalStyles__inputLabel">Bank</span>
                      </Tooltip>
                      <div style={{ marginTop: 8 }}>
                        <Switch
                          checked={entry.bankLevel}
                          onChange={(checked) => onUpdate(index, { bankLevel: checked })}
                        />
                      </div>
                    </div>
                  </div>
                  {(expansionMsg || isExpandedView) && (
                    <div style={{ marginTop: 8 }}>
                      <Alert
                        type="info"
                        showIcon
                        message={
                          expansionMsg ||
                          "Single-instance mapping (not per-unit, per-floor, or bank-level)"
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
